import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Card, List, Space, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { readableInterpretationText, timeText } from '../presentation';

const { Paragraph, Text } = Typography;

const STATUS_META = {
  stable: { label: '表现稳定', color: 'green' },
  acceptable: { label: '整体可接受', color: 'blue' },
  needs_attention: { label: '需要关注', color: 'orange' },
  insufficient_evidence: { label: '证据不足', color: 'default' },
};

const CONFIDENCE_LABELS = {
  high: '高可信',
  medium: '中等可信',
  low: '低可信',
};

const DIMENSION_LABELS = {
  completion: '完成情况',
  mobility: '灵活性',
  strengthPerformance: '力量表现',
  control: '控制能力',
  stability: '稳定性',
  coordination: '协调性',
  agility: '敏捷性',
  powerPerformance: '爆发力',
  data_quality: '数据质量',
  other_features: '其他动作表现',
};

const FINDING_LABELS = {
  strength: '本次亮点',
  priority_issue: '优先关注',
  observation: '动作观察',
};

const PRIORITY_META = {
  high: { label: '优先', color: 'red' },
  medium: { label: '建议', color: 'orange' },
  low: { label: '可选', color: 'blue' },
};

const LIMITATION_LABELS = {
  action_metadata_unavailable: '动作知识暂未获取',
  action_evaluation_config_unavailable: '动作评估标尺暂未获取',
  member_context_unavailable: '会员训练上下文暂未获取',
};

function StatusTag({ status }) {
  const meta = STATUS_META[status] || {
    label: status || '状态未知',
    color: 'default',
  };
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

StatusTag.propTypes = { status: PropTypes.string };
StatusTag.defaultProps = { status: '' };

function EvidenceRefs({ references }) {
  if (!Array.isArray(references) || references.length === 0) return null;
  return (
    <details style={{ marginTop: 8 }}>
      <summary
        style={{
          color: '#8c8c8c',
          cursor: 'pointer',
          fontSize: 12,
          userSelect: 'none',
        }}
      >
        查看证据追踪（{references.length}）
      </summary>
      <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
        {references.map((reference) => (
          <Tag
            key={reference}
            style={{
              marginRight: 0,
              maxWidth: '100%',
              fontSize: 11,
              whiteSpace: 'normal',
              wordBreak: 'break-all',
            }}
          >
            {reference}
          </Tag>
        ))}
      </Space>
    </details>
  );
}

EvidenceRefs.propTypes = { references: PropTypes.arrayOf(PropTypes.string) };
EvidenceRefs.defaultProps = { references: [] };

function PendingState({ status, failureMessage }) {
  if (status === 'pending' || status === 'running') {
    return (
      <Alert
        type="info"
        showIcon
        message={
          status === 'running' ? 'AI 正在解读本次动作' : 'AI 动作解读已进入队列'
        }
        description="客观动作事实已经生成，解读异步执行，不影响下方指标、骨骼回放和课程评估。"
      />
    );
  }
  if (status === 'failed') {
    return (
      <Alert
        type="warning"
        showIcon
        message="AI 动作解读暂未生成"
        description={
          failureMessage ||
          '客观动作事实已经保留，解读失败不会改变本次动作分析结果。'
        }
      />
    );
  }
  if (status === 'succeeded') {
    return (
      <Alert
        type="warning"
        showIcon
        message="AI 动作解读结果暂时无法展示"
        description="服务端已完成解读，但当前页面没有识别到受支持的数据协议；客观动作事实不受影响。"
      />
    );
  }
  return (
    <Alert
      type="info"
      showIcon
      message="这条历史动作记录尚未生成新版 AI 解读"
      description="页面继续展示已经形成的客观动作事实和原有动作点评。"
    />
  );
}

PendingState.propTypes = {
  status: PropTypes.string,
  failureMessage: PropTypes.string,
};
PendingState.defaultProps = { status: '', failureMessage: '' };

export default function ActionInterpretationPanel({
  interpretation,
  status,
  failureMessage,
  generatedAt,
}) {
  const effectiveStatus = status || (interpretation ? 'succeeded' : '');
  if (effectiveStatus !== 'succeeded' || !interpretation) {
    return (
      <Card
        size="small"
        title={
          <Space>
            <RobotOutlined style={{ color: '#1890ff' }} />
            AI 动作解读
          </Space>
        }
      >
        <PendingState
          status={effectiveStatus}
          failureMessage={failureMessage}
        />
      </Card>
    );
  }

  const summary = interpretation.assessmentSummary || {};
  const dimensions = Array.isArray(interpretation.dimensions)
    ? interpretation.dimensions
    : [];
  const visibleDimensions = dimensions.filter(
    (dimension) => dimension.dimensionCode !== 'data_quality',
  );
  const diagnosticDimensions = dimensions.filter(
    (dimension) => dimension.dimensionCode === 'data_quality',
  );
  const findings = Array.isArray(interpretation.findings)
    ? interpretation.findings
    : [];
  const suggestions = Array.isArray(interpretation.suggestions)
    ? interpretation.suggestions
    : [];
  const limitations = Array.isArray(interpretation.limitations)
    ? interpretation.limitations
    : [];

  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <RobotOutlined style={{ color: '#1890ff' }} />
          AI 动作解读
          <Tag color="cyan">基于证据 · 不重算分数</Tag>
        </Space>
      }
      extra={generatedAt ? `生成于 ${timeText(generatedAt)}` : null}
    >
      <div
        style={{
          padding: '14px 16px',
          borderRadius: 8,
          borderLeft: '3px solid #1890ff',
          background: '#f6f9ff',
        }}
      >
        <Space wrap style={{ marginBottom: 8 }}>
          <StatusTag status={summary.status} />
          {summary.confidence && (
            <Tag>
              {CONFIDENCE_LABELS[summary.confidence] || summary.confidence}
            </Tag>
          )}
        </Space>
        <Paragraph style={{ margin: 0, lineHeight: 1.8 }}>
          {readableInterpretationText(summary.text) || '本次动作解读已生成。'}
        </Paragraph>
        <EvidenceRefs references={summary.evidenceRefs} />
      </div>

      {visibleDimensions.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 12,
            marginTop: 16,
          }}
        >
          {visibleDimensions.map((dimension) => (
            <div
              key={dimension.dimensionCode}
              style={{
                padding: '14px 16px',
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                background: '#fff',
              }}
            >
              <Space wrap>
                <Text strong>
                  {DIMENSION_LABELS[dimension.dimensionCode] ||
                    dimension.dimensionCode}
                </Text>
                <StatusTag status={dimension.status} />
                {dimension.confidence && (
                  <Tag>
                    {CONFIDENCE_LABELS[dimension.confidence] ||
                      dimension.confidence}
                  </Tag>
                )}
              </Space>
              <Paragraph style={{ margin: '10px 0 0', lineHeight: 1.7 }}>
                {readableInterpretationText(dimension.summary)}
              </Paragraph>
              <EvidenceRefs references={dimension.evidenceRefs} />
            </div>
          ))}
        </div>
      )}

      {findings.length > 0 && (
        <List
          style={{ marginTop: 16 }}
          header={<Text strong>本次发现</Text>}
          dataSource={findings}
          renderItem={(finding) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  finding.type === 'strength' ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                  )
                }
                title={
                  <Space wrap>
                    <Text strong>{finding.title}</Text>
                    <Tag>{FINDING_LABELS[finding.type] || finding.type}</Tag>
                  </Space>
                }
                description={
                  <div>
                    <Text>
                      {readableInterpretationText(finding.explanation)}
                    </Text>
                    <EvidenceRefs references={finding.evidenceRefs} />
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      {suggestions.length > 0 && (
        <List
          style={{ marginTop: 8 }}
          header={<Text strong>下一次执行建议</Text>}
          dataSource={suggestions}
          renderItem={(suggestion) => {
            const priority = PRIORITY_META[suggestion.priority] || {
              label: suggestion.priority,
              color: 'default',
            };
            return (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <Space align="start">
                    <Tag color={priority.color}>{priority.label}</Tag>
                    <Text>{readableInterpretationText(suggestion.text)}</Text>
                  </Space>
                  <EvidenceRefs references={suggestion.evidenceRefs} />
                </div>
              </List.Item>
            );
          }}
        />
      )}

      {limitations.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="本次解读的数据边界"
          description={limitations
            .map((item) => LIMITATION_LABELS[item] || item)
            .join('；')}
        />
      )}

      {diagnosticDimensions.length > 0 && (
        <details
          style={{
            marginTop: 12,
            padding: '10px 12px',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fafafa',
          }}
        >
          <summary
            style={{
              color: '#8c8c8c',
              cursor: 'pointer',
              fontSize: 13,
              userSelect: 'none',
            }}
          >
            诊断信息 · 数据质量
          </summary>
          {diagnosticDimensions.map((dimension) => (
            <div key={dimension.dimensionCode} style={{ marginTop: 10 }}>
              <Space wrap>
                <Text strong>数据质量</Text>
                <StatusTag status={dimension.status} />
                {dimension.confidence && (
                  <Tag>
                    {CONFIDENCE_LABELS[dimension.confidence] ||
                      dimension.confidence}
                  </Tag>
                )}
              </Space>
              <Paragraph style={{ margin: '8px 0 0', lineHeight: 1.7 }}>
                {readableInterpretationText(dimension.summary)}
              </Paragraph>
              <EvidenceRefs references={dimension.evidenceRefs} />
            </div>
          ))}
        </details>
      )}
    </Card>
  );
}

ActionInterpretationPanel.propTypes = {
  interpretation: PropTypes.shape({
    assessmentSummary: PropTypes.object,
    dimensions: PropTypes.array,
    findings: PropTypes.array,
    suggestions: PropTypes.array,
    limitations: PropTypes.array,
  }),
  status: PropTypes.string,
  failureMessage: PropTypes.string,
  generatedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ActionInterpretationPanel.defaultProps = {
  interpretation: null,
  status: '',
  failureMessage: '',
  generatedAt: null,
};
